import React, { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import { 
  FileText, 
  Clock, 
  User, 
  Award, 
  Handshake, 
  Users 
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { publicAPI } from "../api/publicAPI";
import { advantages } from "../mock"; // Fallback

const Advantages = () => {
  const { t, currentLanguage } = useLanguage();
  const [advantagesContent, setAdvantagesContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAdvantagesContent();
  }, [currentLanguage]);

  const loadAdvantagesContent = async () => {
    try {
      setLoading(true);
      const content = await publicAPI.getPageContent('advantages', currentLanguage);
      setAdvantagesContent(content);
    } catch (err) {
      console.error("Failed to load advantages content from API, using fallback:", err);
      setAdvantagesContent(null);
    } finally {
      setLoading(false);
    }
  };

  const items = advantagesContent?.items || advantages;
  const title = advantagesContent?.title || t('ourAdvantages');

  const getIcon = (iconName) => {
    const icons = {
      FileText: <FileText className="h-8 w-8" />,
      Clock: <Clock className="h-8 w-8" />,
      User: <User className="h-8 w-8" />,
      Award: <Award className="h-8 w-8" />,
      Handshake: <Handshake className="h-8 w-8" />,
      Users: <Users className="h-8 w-8" />
    };
    return icons[iconName] || <FileText className="h-8 w-8" />;
  };

  const getGradientColor = (index) => {
    const gradients = [
      "bg-gradient-to-br from-orange-500 to-orange-600",
      "bg-gradient-to-br from-blue-500 to-blue-600",
      "bg-gradient-to-br from-gray-700 to-gray-800",
      "bg-gradient-to-br from-orange-600 to-red-500",
      "bg-gradient-to-br from-blue-600 to-indigo-600",
      "bg-gradient-to-br from-gray-600 to-gray-700"
    ];
    return gradients[index % gradients.length];
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse text-center mb-16">
            <div className="h-8 bg-gray-300 rounded w-1/2 mx-auto mb-4"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('advantagesDescription')}
          </p>
        </div>

        {/* Advantages grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((advantage, index) => (
            <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-md h-full">
              <CardContent className="p-8">
                <div className="flex flex-col h-full">
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-xl ${getGradientColor(index)} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    {getIcon(advantage.icon)}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-orange-500 transition-colors duration-300">
                      {advantage.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {advantage.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust section */}
        <div className="mt-20">
          <div className="bg-white rounded-2xl p-12 shadow-xl">
            <div className="text-center mb-12">
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                {t('trustRelations')}
              </h3>
              <p className="text-lg text-gray-600 max-w-4xl mx-auto">
                {t('trustDescription')}
              </p>
            </div>

            {/* Government agencies */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {[
                "Минпромторг России",
                "ФСБ России", 
                "ФСТЭК России",
                "Роскомнадзор",
                "МВД России",
                "ГРЧЦ"
              ].map((agency, index) => (
                <div key={index} className="text-center p-4 rounded-lg bg-gray-50 hover:bg-orange-50 transition-colors duration-300">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-500 rounded-lg mx-auto mb-3"></div>
                  <p className="text-sm font-medium text-gray-700">{agency}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Advantages;